import moment from "moment"
import React from "react"
import { Button, Col, Row } from "react-bootstrap"
import { ChevronDown, ChevronUp } from "react-bootstrap-icons"
import BootstrapTable, { ColumnDescription } from "react-bootstrap-table-next"
import filterFactory from "react-bootstrap-table2-filter"
import paginationFactory from "react-bootstrap-table2-paginator"
import ToolkitProvider, { CSVExport } from "react-bootstrap-table2-toolkit"
// import BootstrapTable from "react-bootstrap-table-next";
import { useTranslation } from "react-i18next"
import { ValueAddedServiceModel } from "../../api/pricing.api"
import { useAppDispatch } from "../../app/hooks"
import { SearchBar } from "../../components/Tables/Columns"
import CustomTotal from "../../components/Tables/CustomTotal"
import pageListRenderer from "../../components/Tables/PageListRenderer"
import sizePerPageOptionRenderer from "../../components/Tables/SizePerPageOptionRenderer"

function FufillmentRequestsTable({
    records,
    fulfillmentDetails,
    setFulfillmentDetails,
    setShow,
    setErrors,
    parcels = false
}: {
    records: ValueAddedServiceModel[]
    fulfillmentDetails: Record<string, any>
    setFulfillmentDetails: Function
    setShow: Function
    setErrors: Function
    parcels?: boolean
}) {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    function requestSubmitDate(cell: string) {
        if (cell) return moment(cell).format("DD MMM yyyy")
    }

    const fulfilemtRequestsColumns = [
        {
            text: t("Request ID"),
            hidden: parcels,
            dataField: "fulfillment_id"
        },
        {
            text: t("Request Date"),
            dataField: "created_at",
            hidden: parcels,
            formatter: (cell: string, row: Record<string, any>) => {
                return requestSubmitDate(cell)
            }
        },
        {
            text: t("Parcels"),
            dataField: "parcels",
            hidden: parcels,
            formatter: (cell: Record<string, any>[], row: Record<string, any>) => {
                return cell?.length
            },
            sort: true
        },
        {
            text: t("Customer"),
            dataField: "user",
            hidden: parcels,
            formatter: (cell: Record<string, any>, row: Record<string, any>) => {
                return (
                    <p>
                        {cell?.user_pin}
                        <br />
                        {cell?.name}
                    </p>
                )
            }
        },
        {
            text: t("Items"),
            hidden: parcels,
            dataField: "parcels",
            formatter: (cell: Record<string, any>[], row: Record<string, any>) => {
                return cell?.map((p) => p?.ParcelItems?.length)?.reduce((prev, curr) => prev + curr, 0)
            }
        },
        {
            text: t("QTY"),
            hidden: parcels,
            dataField: "parcels",
            formatter: (cell: Record<string, any>[], row: Record<string, any>) => {
                return (
                    cell
                        ?.map((p) =>
                            p?.ParcelItems?.map((pi: Record<string, any>) => pi?.quantity)?.reduce(
                                (prev: number, curr: number) => Number(prev) + Number(curr),
                                0
                            )
                        )
                        ?.reduce((prev: number, curr: number) => Number(prev) + Number(curr), 0) || 0
                )
            }
        },
        {
            text: t("Suite"),
            dataField: "user",
            hidden: parcels,
            formatter: (cell: Record<string, any>, row: Record<string, any>) => {
                return cell ? cell?.UserSuite?.suite?.suite : "-"
            },
            sort: true
        },
        {
            text: t("Status"),
            dataField: "FulfillmentRequestStatus",
            hidden: parcels,
            formatter: (cell: Record<string, any>, row: Record<string, any>) => {
                return (
                    <div className={`${cell?.name?.toLowerCase()?.replaceAll(" ", "-")}`}>{t(cell?.name)}</div>
                )
            }
        },
        {
            text: t("Parcel ID"),
            dataField: "uuid",
            hidden: !parcels,
            formatter: (cell: Record<string, any>, row: Record<string, any>) => {
                return cell || "-"
            }
        },
        {
            dataField: "",
            hidden: !parcels,
            text: t("Actions"),
            csvExport: false,
            formatter: (cell: number, row: Record<string, any>) => {
                return (
                    <Button
                        onClick={() => {
                            setShow("createParcelModal")
                            setFulfillmentDetails({ ...row })
                        }}
                        className={"primary-bg"}>
                        {t("View")}
                    </Button>
                )
            }
        }
    ]

    const expandRow = {
        renderer: (row: Record<string, any>) => {
            const columns = [
                {
                    dataField: "uuid",
                    text: t("Parcel ID"),
                    classes: "highlighted-text"
                },
                {
                    dataField: "ParcelItems",
                    text: t("QTY"),
                    classes: "highlighted-text",
                    formatter: (cell: Record<string, any>[], parcelRow: Record<string, any>) => {
                        let originalParcelQty = cell
                            ?.map((pi: Record<string, any>) => pi?.quantity)
                            ?.reduce((prev: number, curr: number) => Number(prev) + Number(curr), 0)
                        let splitParcelQty = parcelRow?.FulfilledItems?.map(
                            (pi: Record<string, any>) => pi?.quantity
                        )?.reduce((prev: number, curr: number) => Number(prev) + Number(curr), 0)
                        return originalParcelQty || splitParcelQty || 0
                    }
                },
                {
                    dataField: "ParcelItems",
                    text: t("Action"),
                    classes: "highlighted-text",
                    formatter: (cell: Record<string, any>[], parcelRow: Record<string, any>) => {
                        let parcelQty =
                            cell
                                ?.map((pi: Record<string, any>) => pi?.quantity)
                                ?.reduce((prev: number, curr: number) => Number(prev) + Number(curr), 0) || 0

                        return (
                            <Button
                                onClick={() => {
                                    setShow(
                                        parcelRow?.FulfilledItems?.length > 0 && parcelQty === 0
                                            ? "viewParcel"
                                            : "assignItems"
                                    )
                                    setFulfillmentDetails({ ...parcelRow, fulfillment_request_id: row?.id })
                                }}
                                className={
                                    parcelRow?.FulfilledItems?.length > 0 && parcelQty === 0
                                        ? "secondary-bg"
                                        : "primary-bg"
                                }>
                                {parcelRow?.FulfilledItems?.length > 0 && parcelQty === 0
                                    ? t("View")
                                    : t("Assign to Location")}
                            </Button>
                        )
                    }
                }
            ]
            return (
                <>
                    <BootstrapTable
                        keyField="uuid"
                        data={row?.parcels}
                        columns={columns}
                        wrapperClasses="inner-table my-3 mx-4 "
                        headerClasses="header-class"
                        bordered={false}
                        noDataIndication={t("No_Record")}
                        rowClasses="inner-table-row"
                        condensed={true}
                    />
                </>
            )
        },
        showExpandColumn: !parcels,
        expandByColumnOnly: true,
        expandHeaderColumnRenderer: ({ isAnyExpands }: { isAnyExpands: boolean }) => {
            return <></>
        },
        expandColumnRenderer: ({ expanded }: { expanded: boolean }) => {
            if (expanded) {
                return <ChevronUp color="#2D3748" size={10} className="mx-2 my-3 arrow-icon" />
            }
            return <ChevronDown color="#2D3748" size={10} className="mx-2 my-3 arrow-icon" />
        }
    }

    const options = {
        sizePerPage: 50,
        hideSizePerPage: false,
        hidePageListOnlyOnePage: true,
        sizePerPageOptionRenderer,
        showTotal: true,
        pageListRenderer,
        paginationTotalRenderer: CustomTotal
    }

    const { ExportCSVButton } = CSVExport

    return (
        <div className="table-responsive">
            {fulfilemtRequestsColumns && (
                <ToolkitProvider
                    keyField="id"
                    data={records}
                    columns={fulfilemtRequestsColumns as ColumnDescription<any, any>[]}
                    search={{ searchFormatted: true }}
                    exportCSV={{
                        fileName: "FufillmentRequestsTable.csv",
                        noAutoBOM: false,
                        exportAll: false,
                        onlyExportFiltered: true
                    }}>
                    {(props) => (
                        <div className="table-container my-4">
                            <Row className="search mx-4 valueAddedServicess-search">
                                <Col xs={12} sm={12} md={12} lg={12} className="d-flex justify-content-end">
                                    <SearchBar {...props.searchProps} />
                                </Col>
                                <Col className="d-none">
                                    {/* <ExportCSVButton {...props.csvProps} id="valueAddedServicesExportButton">
                        Export
                    </ExportCSVButton> */}
                                </Col>
                            </Row>
                            <Row className="table-data my-3">
                                <Col>
                                    <BootstrapTable
                                        {...props.baseProps}
                                        expandRow={expandRow}
                                        wrapperClasses="table-wrapper my-3 "
                                        headerClasses="header-class"
                                        bordered={false}
                                        noDataIndication={t("No_Record")}
                                        filter={filterFactory()}
                                        rowClasses="custom-table-row"
                                        condensed={true}
                                        pagination={paginationFactory(options)}
                                        sort={{ dataField: "weight", order: "desc" }}
                                    />
                                </Col>
                            </Row>
                        </div>
                    )}
                </ToolkitProvider>
            )}
        </div>
    )
}

export default FufillmentRequestsTable
